// services/chatService.js - UPDATED VERSION
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Create or get chat with seller
 */
export const createChatWithSeller = async (chatData) => {
  try {
    console.log('🔵 Creating chat...');
    
    // Validate
    if (!chatData.buyerId || !chatData.sellerId || !chatData.productId) {
      throw new Error('Missing required chat information');
    }
    
    // Generate chat ID
    const chatId = `${chatData.buyerId}_${chatData.sellerId}_${chatData.productId}`;
    console.log('Generated Chat ID:', chatId);
    
    const chatRef = doc(db, 'chats', chatId);
    const existingChat = await getDoc(chatRef);
    
    if (existingChat.exists()) {
      console.log('✅ Chat already exists, updating...');
      
      // Update seller unread count
      await updateDoc(chatRef, {
        lastMessage: chatData.initialMessage || 'New message',
        lastMessageTime: serverTimestamp(),
        sellerUnread: increment(1),
        updatedAt: serverTimestamp()
      });
      
      return chatId;
    }
    
    // Create new chat
    const chatDoc = {
      id: chatId,
      productId: chatData.productId,
      productName: chatData.productName || 'Product Inquiry',
      productImage: chatData.productImage || '',
      
      // Buyer info
      buyerId: chatData.buyerId,
      buyerName: chatData.buyerName || 'Buyer',
      buyerEmail: chatData.buyerEmail || '',
      buyerPhoto: chatData.buyerPhoto || '',
      
      // Seller info
      sellerId: chatData.sellerId,
      sellerName: chatData.sellerName || 'Seller',
      sellerEmail: chatData.sellerEmail || '',
      sellerPhoto: chatData.sellerPhoto || '',
      sellerVerified: chatData.sellerVerified || false,
      
      // Chat metadata
      status: 'active',
      lastMessage: chatData.initialMessage || 'New conversation started',
      lastMessageTime: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Unread counts - Seller has unread
      buyerUnread: 0,
      sellerUnread: 1,
      
      // Participants array for querying
      participants: [chatData.buyerId, chatData.sellerId],
      
      // Participants info
      participantsInfo: {
        [chatData.buyerId]: {
          name: chatData.buyerName || 'Buyer',
          email: chatData.buyerEmail || '',
          role: 'buyer'
        },
        [chatData.sellerId]: {
          name: chatData.sellerName || 'Seller',
          email: chatData.sellerEmail || '',
          role: 'seller'
        }
      }
    };
    
    await setDoc(chatRef, chatDoc);
    console.log('✅ New chat created');
    
    // Send initial message
    if (chatData.initialMessage) {
      await sendMessage(chatId, {
        senderId: chatData.buyerId,
        senderName: chatData.buyerName || 'Buyer',
        senderRole: 'buyer',
        message: chatData.initialMessage,
        type: 'inquiry',
        productDetails: {
          productId: chatData.productId,
          productName: chatData.productName,
          quantity: chatData.quantity || 1,
          unit: chatData.unit || 'PCS',
          totalPrice: chatData.totalPrice || 0,
          moq: chatData.moq || 1,
          unitPrice: chatData.unitPrice || 0
        }
      });
    }
    
    return chatId;
    
  } catch (error) {
    console.error('❌ Error creating chat:', error);
    throw error;
  }
};

/**
 * Get user's chats - FIXED VERSION
 */
export const getUserChats = async (userId, role = null) => {
  try {
    console.log('🔍 getUserChats called for userId:', userId, 'role:', role);
    
    if (!userId) {
      console.error('❌ No userId provided');
      return [];
    }
    
    const chatsRef = collection(db, 'chats');
    
    // Query using participants array
    const q = query(
      chatsRef,
      where('participants', 'array-contains', userId),
      where('status', '==', 'active')
    );
    
    console.log('📊 Executing query...');
    const snapshot = await getDocs(q);
    console.log(`📊 Found ${snapshot.size} documents in query`);
    
    const chats = [];
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      console.log('📄 Processing chat:', {
        id: docSnap.id,
        buyerId: data.buyerId,
        sellerId: data.sellerId,
        lastMessage: data.lastMessage
      });
      
      // Determine if user is buyer or seller
      const isBuyer = data.buyerId === userId;
      const isSeller = data.sellerId === userId;
      
      if (isBuyer || isSeller) {
        const unreadCount = isBuyer ? (data.buyerUnread || 0) : (data.sellerUnread || 0);
        const otherUser = isBuyer ? {
          id: data.sellerId,
          name: data.sellerName,
          email: data.sellerEmail,
          role: 'seller'
        } : {
          id: data.buyerId,
          name: data.buyerName,
          email: data.buyerEmail,
          role: 'buyer'
        };
        
        chats.push({
          id: docSnap.id,
          ...data,
          lastMessageTime: data.lastMessageTime?.toDate?.() || new Date(),
          createdAt: data.createdAt?.toDate?.() || new Date(),
          unreadCount: unreadCount,
          otherUser: otherUser,
          myRole: isBuyer ? 'buyer' : 'seller'
        });
        
        console.log('✅ Added chat to list:', docSnap.id);
      }
    });
    
    // Sort by lastMessageTime
    chats.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
    
    console.log(`✅ Returning ${chats.length} chats for user ${userId}`);
    return chats;
    
  } catch (error) {
    console.error('❌ Error in getUserChats:', error);
    
    // Fallback: Try a simpler approach
    try {
      console.log('🔄 Trying fallback method...');
      const chatsRef = collection(db, 'chats');
      const q = query(chatsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const allChats = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        
        // Manual filtering
        if (data.buyerId === userId || data.sellerId === userId) {
          allChats.push({
            id: docSnap.id,
            ...data,
            lastMessageTime: data.lastMessageTime?.toDate?.() || new Date(),
            unreadCount: data.buyerId === userId ? (data.buyerUnread || 0) : (data.sellerUnread || 0)
          });
        }
      });
      
      console.log(`🔄 Fallback found ${allChats.length} chats`);
      return allChats;
      
    } catch (fallbackError) {
      console.error('❌ Fallback also failed:', fallbackError);
      return [];
    }
  }
};

/**
 * Send a message
 */
export const sendMessage = async (chatId, messageData) => {
  try {
    console.log('📤 Sending message to chat:', chatId);
    
    if (!chatId || !messageData.senderId || !messageData.message) {
      throw new Error('Missing message data');
    }
    
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const messageDoc = {
      id: messageId,
      chatId: chatId,
      senderId: messageData.senderId,
      senderName: messageData.senderName || 'User',
      senderRole: messageData.senderRole || 'buyer',
      message: messageData.message,
      type: messageData.type || 'text',
      timestamp: serverTimestamp(),
      readBy: [messageData.senderId],
      
      ...(messageData.productDetails && {
        productDetails: messageData.productDetails
      })
    };
    
    // Save message
    const messageRef = doc(db, 'chats', chatId, 'messages', messageId);
    await setDoc(messageRef, messageDoc);
    
    // Update chat metadata
    const chatRef = doc(db, 'chats', chatId);
    const updateData = {
      lastMessage: messageData.message,
      lastMessageTime: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Update unread count for receiver
    if (messageData.senderRole === 'buyer') {
      updateData.sellerUnread = increment(1);
    } else if (messageData.senderRole === 'seller') {
      updateData.buyerUnread = increment(1);
    }
    
    await updateDoc(chatRef, updateData);
    console.log('✅ Message sent and chat updated');
    
    return messageId;
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    throw error;
  }
};

/**
 * Real-time chat subscription
 */
export const subscribeToChatMessages = (chatId, callback) => {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || new Date()
        });
      });
      callback(messages);
    }, (error) => {
      console.error('❌ Chat subscription error:', error);
      callback([]);
    });
    
    return unsubscribe;
    
  } catch (error) {
    console.error('❌ Error setting up subscription:', error);
    return () => {};
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (chatId, userId) => {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) return;
    
    const chatData = chatSnap.data();
    const isBuyer = chatData.buyerId === userId;
    
    // Reset unread count
    await updateDoc(chatRef, {
      ...(isBuyer ? { buyerUnread: 0 } : { sellerUnread: 0 }),
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Messages marked as read for user ${userId}`);
    
  } catch (error) {
    console.error('❌ Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Get chat by ID
 */
export const getChatById = async (chatId) => {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      throw new Error('Chat not found');
    }
    
    const data = chatSnap.data();
    return {
      id: chatSnap.id,
      ...data,
      lastMessageTime: data.lastMessageTime?.toDate?.() || new Date(),
      createdAt: data.createdAt?.toDate?.() || new Date()
    };
    
  } catch (error) {
    console.error('❌ Error getting chat:', error);
    throw error;
  }
};